<template>
  <div class="flex w-full flex-col">
    <div v-if="title" class="text-xl font-semibold">{{ title }}</div>
    <component
      :is="Comp"
      :nodeType="nodeType"
      :module="currentModule"
      :key="currentModule?.code || props.dataType"
      @update="(payload) => updateModuleInStore(payload)"></component>
  </div>
</template>

<script setup>
import { useDataValidation } from '@/composables/useDataValidation';
import { SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules.js';
import { useLedPanelStore } from '@/stores/api/ledpanel';
import { useProjectorStore } from '@/stores/api/projector';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection.js';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { render } from '@/threejs/core';
import { handleObjectLockChange } from '@/threejs/handlers/transform';
import { getObjectByCode } from '@/threejs/objectManager';
import { computeRotationFromPositionTarget } from '@/utils/projectorCalculations';
import { useConversions } from '@/utils/useConversions.js';
import * as THREE from 'three';
import { computed } from 'vue';
import SelectedNodeComponent from './SelectedNodeComponent.vue';
import Settings from './Settings.vue';

const { average } = useConversions();

const props = defineProps({
  dataType: {
    type: String,
    default: 'selectedNode',
  },
  nodeType: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
});

const cruxModuleStore = useCruxModuleStore();
const selectionStore = useSelectionStore();
const appStore = useAppStore();
const { sanitizeModuleData } = useDataValidation();
const projectorStore = useProjectorStore();
const ledPanelStore = useLedPanelStore();
const timelinePlayerStore = useTimelinePlayerStore();
const projectorTargetInputSessions = new Map();
let internalRotationUpdateDepth = 0;

const currentModule = computed(() => {
  switch (props.dataType) {
    case SETTING_PANEL_TABS.PROJECT_SETTINGS:
      return cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.PROJECT_SETTINGS);
    case SETTING_PANEL_TABS.VIEWPORT_SETTINGS:
      return cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.VIEWPORT_SETTINGS);
    case 'selectedNode':
      // Find the crux module that matches the selected object ID
      if (selectionStore.selectedObjectId) {
        return cruxModuleStore.cruxModules.find((module) => module.code === selectionStore.selectedObjectId);
      }
      return null;
    default:
      return null;
  }
});

const Comp = computed(() => {
  switch (props.dataType) {
    case 'project_settings':
    case 'viewport_settings':
      return Settings;
    case 'selectedNode':
      return SelectedNodeComponent;
    default:
      return null;
  }
});

const getProjectorTargetSessionKey = (moduleCode, axis) => `${moduleCode}:${axis}`;

const getProjectorDisplayRotation = (projector, fallbackRollDeg = 0) => {
  return {
    x: projector.rotation.x * (180 / Math.PI),
    y: projector.rotation.y * (180 / Math.PI),
    z: projector.userData?.roll != null ? projector.userData.roll * (180 / Math.PI) : fallbackRollDeg,
  };
};

const startProjectorTargetInputSession = (module, axis, rawValue) => {
  const projector = getObjectByCode(module.code);
  if (!projector) return;

  const lensWorld = projector.getLensWorldPosition();
  const currentTarget =
    projector.userData.target?.clone?.() ??
    new THREE.Vector3(module.data.target?.x ?? 0, module.data.target?.y ?? 0, module.data.target?.z ?? 0);
  let targetOffset = currentTarget.sub(lensWorld);

  if (targetOffset.lengthSq() < 0.000001) {
    targetOffset = new THREE.Vector3(0, 0, -1);
  }

  projectorTargetInputSessions.set(getProjectorTargetSessionKey(module.code, axis), {
    axis,
    lastRawValue: rawValue,
    throwDistance: Math.max(targetOffset.length(), 0.01),
    throwSign: 1,
    roll: projector.userData.roll || 0,
    lastVerticalAxis: new THREE.Vector3(1, 0, 0).applyQuaternion(projector.quaternion).normalize(),
  });
};

const endProjectorTargetInputSession = (module, axis) => {
  const key = getProjectorTargetSessionKey(module.code, axis);
  const session = projectorTargetInputSessions.get(key);
  projectorTargetInputSessions.delete(key);
  return session;
};

const applyOrbitOffset = (projector, module, offset) => {
  const lensWorld = projector.getLensWorldPosition();
  const newTarget = lensWorld.clone().add(offset);
  projector.userData.target.copy(newTarget);
  projector.rotateToTarget(projector.userData.target);

  const targetMesh = getObjectByCode(module.code + '-target');
  if (targetMesh) targetMesh.position.copy(projector.userData.target);
  projector.updateMatrixWorld(true);

  module.data.target = { x: newTarget.x, y: newTarget.y, z: newTarget.z };
  module.data.rotation = getProjectorDisplayRotation(projector, module.data.rotation?.z ?? 0);
  render();
};

const updateModuleInStore = (payload) => {
  appStore.setDisplayText('Auto Saving...');
  if (props.dataType === 'selectedNode' && selectionStore.selectedObjectId) {
    // For selected nodes, update the specific module by its code
    const module = cruxModuleStore.cruxModules.find((m) => m.code === selectionStore.selectedObjectId);
    if (module) {
      if (payload.phase && module.type === 'projector' && payload.key?.startsWith('target.')) {
        const axis = payload.key.split('.').pop();

        if (payload.phase === 'start') {
          if (!module.data.lockTarget) {
            startProjectorTargetInputSession(module, axis, payload.value);
          }
          return;
        }

        if (payload.phase === 'change') {
          return;
        }

        if (payload.phase === 'end') {
          const session = endProjectorTargetInputSession(module, axis);
          if (!session || module.data.lockTarget) return;

          const projector = getObjectByCode(module.code);
          if (!projector?.userData?.target) return;

          const committedModule = sanitizeModuleData({
            ...module,
            data: {
              ...module.data,
              target: {
                x: projector.userData.target.x,
                y: projector.userData.target.y,
                z: projector.userData.target.z,
              },
              rotation: getProjectorDisplayRotation(projector, module.data.rotation?.z ?? 0),
            },
          });

          cruxModuleStore.update(module.code, committedModule);
          return;
        }
      }

      if (payload.isRoot === true) {
        // Update root property directly
        const updatedModule = sanitizeModuleData({
          ...module,
          [payload.key]: payload.value,
        });

        cruxModuleStore.update(module.code, updatedModule);
      } else if (payload.key === 'libraryCode') {
        let result;
        if (module.type === 'projector') {
          result = projectorStore.getProjectorDataByCode(payload.value);
        } else {
          result = ledPanelStore.getLedPanelDataByCode(payload.value);
        }
        if (result) {
          const { code, ...libraryData } = result;
          const updatedModule = {
            ...module,
            data: {
              ...module.data,
              ...libraryData,
              libraryCode: payload.value,
            },
          };
          cruxModuleStore.update(module.code, updatedModule);
        } else {
          console.warn('No projector or LED panel found with code:', payload.value);
        }
      } else {
        // convert key from position.x to nested object
        const keys = payload.key.split('.');
        let updatedData = { ...module.data };
        // Deep-clone nested objects along the key path to avoid mutating the reactive store
        if (keys.length > 1 && keys[0] in updatedData && typeof updatedData[keys[0]] === 'object') {
          updatedData[keys[0]] = { ...updatedData[keys[0]] };
        }
        let current = updatedData;
        if (payload.key === 'lock') {
          const mesh = getObjectByCode(module.code);
          if (mesh) {
            mesh.userData.lock = payload.value;
          }
          handleObjectLockChange(module.code, payload.value);
        }
        if (payload.key === 'lockTarget') {
          const mesh = getObjectByCode(module.code);
          if (mesh) {
            mesh.userData.lockTarget = payload.value;
          }
        }
        if (payload.key === 'lock_scale') {
          const mesh = getObjectByCode(module.code);
          if (mesh) {
            mesh.userData.lock_scale = payload.value;
          }
          if (payload.value === false && typeof current.scale === 'number') {
            current.scale = {
              x: current.scale,
              y: current.scale,
              z: current.scale,
            };
          } else if (payload.value === true && typeof current.scale === 'object') {
            const uniformScale = average([current.scale.x ?? 1, current.scale.y ?? 1, current.scale.z ?? 1]);
            current.scale = uniformScale;
          }
        }
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!(key in current)) {
            current[key] = {};
          }
          current = current[key];
        }
        const changedAxis = keys[keys.length - 1];
        // For groups: capture old values before mutation (shallow copy shares the same nested objects)
        let groupPositionDelta = 0;
        let groupPositionAxis = null;
        let groupRotationDelta = 0;
        let groupRotationAxis = null;
        if (module.type === 'group' && payload.key.startsWith('position.')) {
          groupPositionAxis = keys[keys.length - 1];
          const oldVal = module.data.position?.[groupPositionAxis] ?? 0;
          groupPositionDelta = payload.value - oldVal;
        }
        if (module.type === 'group' && payload.key.startsWith('rotation.')) {
          groupRotationAxis = keys[keys.length - 1];
          const oldVal = module.data.rotation?.[groupRotationAxis] ?? 0;
          groupRotationDelta = payload.value - oldVal;
        }

        // Save old values BEFORE mutation (line below mutates module.data via shared reference)
        const oldPositionValue =
          module.type === 'projector' && payload.key.startsWith('position.')
            ? (module.data.position?.[changedAxis] ?? 0)
            : null;

        // Save old target values before mutation for orbit constraint
        let savedOldTarget = null;
        if (module.type === 'projector' && payload.key.startsWith('target.')) {
          savedOldTarget = { ...module.data.target };
        }

        // Block target edits when lockTarget is on
        if (module.type === 'projector' && payload.key.startsWith('target.') && module.data.lockTarget) {
          return;
        }

        // Sanitize numeric values — empty inputs default to 0
        if (
          payload.key.startsWith('position.') ||
          payload.key.startsWith('target.') ||
          payload.key.startsWith('rotation.')
        ) {
          const num = Number(payload.value);
          if (!isFinite(num)) payload.value = 0;
        }

        // Some numeric inputs can emit focus/blur commits without a real value change.
        // For projector transforms, ignore exact no-op writes so we don't reapply
        // alternate Euler orientations when the user only changes focus.
        if (
          module.type === 'projector' &&
          (payload.key.startsWith('position.') ||
            payload.key.startsWith('target.') ||
            payload.key.startsWith('rotation.'))
        ) {
          const currentStoredValue = module.data?.[keys[0]]?.[changedAxis];
          if (typeof currentStoredValue === 'number' && currentStoredValue === payload.value) {
            return;
          }
        }

        current[changedAxis] = payload.value;

        // For projector target inputs during active edit, treat numeric drags as
        // relative orbit controls instead of absolute world coordinates.
        if (savedOldTarget && updatedData.position && updatedData.target) {
          const projector = getObjectByCode(module.code);
          const session = projectorTargetInputSessions.get(getProjectorTargetSessionKey(module.code, changedAxis));

          if (projector && session) {
            const delta = payload.value - session.lastRawValue;

            if (Math.abs(delta) > 0.000001) {
              const lensWorld = projector.getLensWorldPosition();
              let currentOffset = projector.userData.target.clone().sub(lensWorld);

              if (currentOffset.lengthSq() < 0.000001) {
                currentOffset = new THREE.Vector3(0, 0, -1).multiplyScalar(session.throwDistance);
              }

              const throwDistance = session.throwDistance;

              if (changedAxis === 'x') {
                const horizontalRadius = Math.max(Math.hypot(currentOffset.x, currentOffset.z), 0.001);
                const rotationAxis = new THREE.Vector3(0, 1, 0);
                const angle = delta / horizontalRadius;
                session.lastRawValue = payload.value;

                if (Math.abs(angle) > 0.000001) {
                  currentOffset.applyQuaternion(new THREE.Quaternion().setFromAxisAngle(rotationAxis, angle));
                  currentOffset.normalize().multiplyScalar(throwDistance);
                  applyOrbitOffset(projector, module, currentOffset);
                }
              } else if (changedAxis === 'z') {
                // Z sets world coordinate directly — matches gizmo target drag.
                // The projector rotates to face the new target and flips 180°
                // if the target crosses through the lens position.
                const newTarget = projector.userData.target.clone();
                newTarget.z = payload.value;

                const newOffset = newTarget.clone().sub(lensWorld);
                if (newOffset.lengthSq() < 0.000001) {
                  newOffset.set(0, 0, -1).multiplyScalar(0.01);
                }

                session.throwDistance = newOffset.length();
                session.lastRawValue = payload.value;
                applyOrbitOffset(projector, module, newOffset);
              } else if (changedAxis === 'y') {
                // Use the right vector captured at session start so the orbit
                // traces a stable great circle without pole singularities or
                // roll drift from rotateToTarget feedback.
                const rotationAxis = session.lastVerticalAxis.clone();
                const angle = delta / throwDistance;
                session.lastRawValue = payload.value;

                if (Math.abs(angle) > 0.000001) {
                  currentOffset.applyQuaternion(new THREE.Quaternion().setFromAxisAngle(rotationAxis, angle));
                  currentOffset.normalize().multiplyScalar(throwDistance);
                  applyOrbitOffset(projector, module, currentOffset);
                }
              }
            }

            return;
          }

          // Fallback for non-session updates: keep target on the same throw-distance sphere.
          const pos = updatedData.position;
          const oldDx = (savedOldTarget.x ?? 0) - (pos.x ?? 0);
          const oldDy = (savedOldTarget.y ?? 0) - (pos.y ?? 0);
          const oldDz = (savedOldTarget.z ?? 0) - (pos.z ?? 0);
          const throwDist = Math.max(Math.sqrt(oldDx * oldDx + oldDy * oldDy + oldDz * oldDz), 0.01);

          const dirX = (updatedData.target.x ?? 0) - (pos.x ?? 0);
          const dirY = (updatedData.target.y ?? 0) - (pos.y ?? 0);
          const dirZ = (updatedData.target.z ?? 0) - (pos.z ?? 0);
          const dirLen = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ);

          if (dirLen > 0.001) {
            const scale = throwDist / dirLen;
            updatedData.target = { ...updatedData.target };
            updatedData.target.x = (pos.x ?? 0) + dirX * scale;
            updatedData.target.y = (pos.y ?? 0) + dirY * scale;
            updatedData.target.z = (pos.z ?? 0) + dirZ * scale;
          }
        }

        // For projectors: apply position to Three.js directly (same as gizmo translate)
        if (module.type === 'projector' && payload.key.startsWith('position.')) {
          const projector = getObjectByCode(module.code);
          if (projector) {
            const newPos = updatedData.position;
            projector.position.set(newPos.x ?? 0, newPos.y ?? 0, newPos.z ?? 0);

            if (!module.data.lockTarget) {
              // Move target with position by same delta (matches gizmo translate behavior)
              const delta = payload.value - oldPositionValue;
              if (delta !== 0 && projector.userData.target) {
                projector.userData.target[changedAxis] += delta;
                const targetMesh = getObjectByCode(module.code + '-target');
                if (targetMesh) targetMesh.position.copy(projector.userData.target);
              }
              projector.rotateToTarget(projector.userData.target);
              projector.updateMatrixWorld(true);

              // Update module.data directly for panel display
              module.data.position = { x: newPos.x ?? 0, y: newPos.y ?? 0, z: newPos.z ?? 0 };
              module.data.target = {
                x: projector.userData.target.x,
                y: projector.userData.target.y,
                z: projector.userData.target.z,
              };
              module.data.rotation = getProjectorDisplayRotation(projector, module.data.rotation?.z ?? 0);
            } else {
              // lockTarget: keep target fixed, re-orient to face it
              projector.rotateToTarget(projector.userData.target);
              projector.updateMatrixWorld(true);

              module.data.position = { x: newPos.x ?? 0, y: newPos.y ?? 0, z: newPos.z ?? 0 };
              module.data.rotation = getProjectorDisplayRotation(projector, module.data.rotation?.z ?? 0);
            }
            render();
          }
          return;
        }

        // For projectors: apply rotation to Three.js directly (no store update to avoid DInputNumber loop)
        // The store persists on blur/commit via @valueChange → saveValue
        if (module.type === 'projector' && payload.key.startsWith('rotation.') && internalRotationUpdateDepth === 0) {
          const projector = getObjectByCode(module.code);
          if (projector) {
            const currentRot = module.data.rotation || { x: 0, y: 0, z: 0 };

            const rot = {
              x: changedAxis === 'x' ? payload.value : (currentRot.x ?? 0),
              y: changedAxis === 'y' ? payload.value : (currentRot.y ?? 0),
              z: changedAxis === 'z' ? payload.value : (currentRot.z ?? 0),
            };

            // Set rotation directly on Three.js
            const xRad = rot.x * (Math.PI / 180);
            const yRad = rot.y * (Math.PI / 180);
            const zRad = rot.z * (Math.PI / 180);
            projector.rotation.set(xRad, yRad, zRad);
            projector.userData.roll = zRad;

            if (!module.data.lockTarget) {
              // Move target to follow the new direction (preserve throw distance from lens)
              const lensWorld = projector.getLensWorldPosition();
              const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(projector.quaternion);
              const throwDist = Math.max(lensWorld.distanceTo(projector.userData.target), 0.01);
              const newTarget = lensWorld.clone().add(forward.multiplyScalar(throwDist));
              projector.userData.target.copy(newTarget);
              const targetMesh = getObjectByCode(module.code + '-target');
              if (targetMesh) targetMesh.position.copy(newTarget);

              projector.updateMatrixWorld();

              // Update module.data directly for panel display (no store.update to avoid loop)
              module.data.rotation = rot;
              module.data.target = { x: newTarget.x, y: newTarget.y, z: newTarget.z };
            } else {
              // lockTarget: keep target fixed, just re-orient to face it
              projector.rotateToTarget(projector.userData.target);
              projector.updateMatrixWorld();
              module.data.rotation = rot;
            }
          }
          return;
        }

        // For projectors with lockTarget off: move target with position (like gizmo does)
        if (oldPositionValue !== null && !updatedData.lockTarget) {
          const delta = payload.value - oldPositionValue;
          if (delta !== 0 && updatedData.target) {
            updatedData.target = { ...updatedData.target };
            updatedData.target[changedAxis] = (updatedData.target[changedAxis] ?? 0) + delta;
          }
        }

        // For projectors: apply position change to Three.js directly for immediate visual feedback
        // (mirrors how rotation changes are handled above — the store→watcher path is async)
        if (module.type === 'projector' && payload.key.startsWith('position.')) {
          const projector = getObjectByCode(module.code);
          if (projector) {
            projector.position.set(
              updatedData.position.x ?? 0,
              updatedData.position.y ?? 0,
              updatedData.position.z ?? 0
            );

            if (updatedData.target) {
              projector.userData.target.set(
                updatedData.target.x ?? 0,
                updatedData.target.y ?? 0,
                updatedData.target.z ?? 0
              );
              const targetMesh = getObjectByCode(module.code + '-target');
              if (targetMesh) targetMesh.position.copy(projector.userData.target);
            }

            projector.rotateToTarget(projector.userData.target);
            projector.updateMatrixWorld(true);
            render();
          }
        }

        // For projectors: recompute rotation from position+target when either changes
        // This keeps module.data.rotation in sync (used by the panel inputs)
        if (
          module.type === 'projector' &&
          (payload.key.startsWith('position.') || payload.key.startsWith('target.')) &&
          updatedData.position &&
          updatedData.target
        ) {
          const newRot = computeRotationFromPositionTarget(
            updatedData.position,
            updatedData.target,
            updatedData.rotation?.z ?? 0
          );
          if (newRot) {
            internalRotationUpdateDepth++;
            updatedData.rotation = newRot;
          }
        }

        // For groups: move child projector targets when group position changes
        if (groupPositionAxis && groupPositionDelta !== 0) {
          const axis = groupPositionAxis;
          const delta = groupPositionDelta;
          const children = cruxModuleStore.cruxModules.filter(
            (m) => m.data?.parentCode === module.code && m.type === 'projector' && m.data?.target
          );
          children.forEach((child) => {
            // Update store data for the child target
            child.data.target = { ...child.data.target };
            child.data.target[axis] = (child.data.target[axis] ?? 0) + delta;

            // Update the Three.js projector target and its mesh directly
            const projectorObj = getObjectByCode(child.code);
            if (projectorObj && projectorObj.userData.target) {
              projectorObj.userData.target[axis] += delta;
              // Move the ProjectorTarget mesh in the scene
              const targetMesh = getObjectByCode(child.code + '-target');
              if (targetMesh) {
                targetMesh.position[axis] += delta;
              }
            }

            // Persist child target change through the store
            cruxModuleStore.update(
              child.code,
              sanitizeModuleData({ ...child, data: { ...child.data, target: child.data.target } })
            );
          });
        }
        // For groups: rotate child projector targets when group rotation changes
        if (groupRotationAxis && groupRotationDelta !== 0) {
          const axis = groupRotationAxis;
          const angleDelta = groupRotationDelta;
          if (angleDelta !== 0) {
            const groupPos = module.data.position || { x: 0, y: 0, z: 0 };
            const groupCenter = new THREE.Vector3(groupPos.x ?? 0, groupPos.y ?? 0, groupPos.z ?? 0);

            // Build a quaternion for the rotation delta around the changed axis
            const deltaQuat = new THREE.Quaternion();
            const angleRad = THREE.MathUtils.degToRad(angleDelta);
            if (axis === 'x') deltaQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), angleRad);
            else if (axis === 'y') deltaQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), angleRad);
            else if (axis === 'z') deltaQuat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), angleRad);

            const children = cruxModuleStore.cruxModules.filter(
              (m) => m.data?.parentCode === module.code && m.type === 'projector' && m.data?.target
            );
            children.forEach((child) => {
              const target = new THREE.Vector3(
                child.data.target.x ?? 0,
                child.data.target.y ?? 0,
                child.data.target.z ?? 0
              );
              const offset = target.sub(groupCenter);
              offset.applyQuaternion(deltaQuat);
              const newTarget = groupCenter.clone().add(offset);

              child.data.target = { x: newTarget.x, y: newTarget.y, z: newTarget.z };

              const projectorObj = getObjectByCode(child.code);
              if (projectorObj && projectorObj.userData.target) {
                projectorObj.userData.target.copy(newTarget);
                const targetMesh = getObjectByCode(child.code + '-target');
                if (targetMesh) targetMesh.position.copy(newTarget);
              }

              cruxModuleStore.update(
                child.code,
                sanitizeModuleData({ ...child, data: { ...child.data, target: child.data.target } })
              );
            });
          }
        }

        // When a new source asset is assigned to a projector/LED, initialize media block defaults and reset timeline
        if (
          payload.key === 'source' &&
          payload.value &&
          (module.type === 'projector' || module.type === 'led_screen')
        ) {
          if (!updatedData.mediaStartTime && updatedData.mediaStartTime !== 0) {
            updatedData.mediaStartTime = 0;
          }
          if (!updatedData.mediaDuration) {
            updatedData.mediaDuration = 10;
          }
          if (!updatedData.mediaStart && updatedData.mediaStart !== 0) {
            updatedData.mediaStart = 0;
          }
          timelinePlayerStore.setCurrentTime(0);
        }

        const updatedModule = sanitizeModuleData({
          ...module,
          data: updatedData,
        });

        cruxModuleStore.update(module.code, updatedModule);
        if (internalRotationUpdateDepth > 0) {
          internalRotationUpdateDepth--;
        }
      }
    }
  } else {
    // For other data types (project_settings, viewport_settings)
    cruxModuleStore.updateModuleData(props.dataType, payload.key, payload.value);
  }
};
</script>
